import { CommonModule } from "@angular/common";
import { Component, Injector, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { TableModule } from "primeng/table";
import { ProfileService } from "src/services/profile/profile.service";
import { MatDialog } from "@angular/material/dialog";
import { first } from "rxjs";
import { ProgressBarModule } from "primeng/progressbar";
import { MenuModule } from "primeng/menu";
import { CBox_GetLockerBaseData } from "@server/services/cbox/public/api/v1/resources/locker/types";
import { CalendarModule } from "primeng/calendar";
import { MultiSelectModule } from "primeng/multiselect";
import {
  CBox_PublicGetAgentActivityDataListItem,
  CBox_PublicGetAgentActivityDataListParams,
  CBox_PublicGetShipmentActivityDataListParams,
  CBox_PublicGetPickUpTimesActivityDataResponse,
  CBox_PublicShipmentActivityDetails,
  CBox_PublicShipmentActivityUserDropOffAction,
  CBox_PublicShipmentActivityUserPickUpAction,
} from "@server/services/cbox/public/api/v1/resources/reports/types";
import { lockerCleanAddress } from "src/helpers/functions";
import { CBoxProfilePickupRequestInfoComponent } from "app/profile/requests/pickup/dialogs/info/cbox-profile-pickup-request-info.component";
import { CBoxProfileDropOffRequestInfoComponent } from "app/profile/requests/drop-off/dialogs/info/cbox-profile-dropoff-request-info.component";
import { TableWithFilters } from "app/shared/table-with-filters";
import { CBoxProfilePickUpTimesActivityReportGenerateDialogComponent } from "../dialogs/generate/pick-up-times-activity/cbox-profile-pick-up-times-activity-report-generate-dialog.component";

interface Bucket {
  cumulatedPercentageFromTotal: number;
  label: string;
  count: number;
  requests: {
    responseId: string;
    uniqueReference: string;
    awb: string;
  }[];
}

interface SummaryRow {
  totalDelivered: number;
  totalRetrieved: number;
  totalPickedUp: number;
  totalCancelled: number;
  interval_0_2: number;
  interval_2_4: number;
  interval_4_12: number;
  interval_12_24: number;
  interval_24_36: number;
  interval_36plus: number;
}

@Component({
  selector: "app-cbox-profile-pick-up-times-activity-reports-list",
  templateUrl: "cbox-profile-pick-up-times-activity-reports-list.component.html",
  styleUrls: ["./cbox-profile-pick-up-times-activity-reports-list.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    ProgressBarModule,
    MenuModule,
    CalendarModule,
    MultiSelectModule
  ]
})

export class CBoxProfilePickUpTimesActivityReportsListComponent extends TableWithFilters<CBox_PublicGetPickUpTimesActivityDataResponse, CBox_PublicGetShipmentActivityDataListParams> {

  filtersForm: FormGroup;
  dataFetchUrl = "reports/pick-up-times/activity";
  sessionFiltersKey = "profile/reports/pick-up-times/filters";
  sessionFiltersToSave = ["page", "interval"];
  localStorageFiltersKey = "profile/reports/pick-up-times/filters";
  localStorageFiltersToSave = ["pageSize"];
  defaultFilters = signal({
    lockers: [],
    page: 1,
    pageSize: 25,
  });
  scrollPosition = signal(0);
  summary = signal<SummaryRow | undefined>(undefined);

  constructor(
    inj: Injector,
    private profileService: ProfileService,
    private fb: FormBuilder,
    private dialog: MatDialog) {
      super(inj);
      this.filtersForm = this.fb.group({
        page: [1],
        pageSize: [25],
        lockers: [[]],
        interval: [[this.oneMonthAgo, this.today]]
      });
    }

  async init(): Promise<void> {
    this.profileService.setTitle("Rapoarte lockere");
  }

  getActionCount(type: CBox_PublicGetAgentActivityDataListItem["actions"]["counts"][0]["type"], actions: CBox_PublicGetAgentActivityDataListItem["actions"]["counts"]): number {
    if (!actions) {
      return 0;
    }
    return actions.find((a: CBox_PublicGetAgentActivityDataListItem["actions"]["counts"][0]) => a.type === type)?.count ?? 0;
  }

  lockerCleanAddress(addressParts: CBox_GetLockerBaseData["addressParts"]): string {
    if (!addressParts) {
      return "";
    }
    return lockerCleanAddress(addressParts);
  }

  //  toggleLockerExpansion(report: any): void {
  //   if (this.expandedLockers.has(report)) {
  //     this.expandedLockers.delete(report);
  //   } else {
  //     this.expandedLockers.add(report);
  //   }
  // }

  // isLockerExpanded(report: any): boolean {
  //   return this.expandedLockers.has(report);
  // }

  // toggleShipmentExpansion(shipment: any): void {
  //   if (this.expandedShipments.has(shipment)) {
  //     this.expandedShipments.delete(shipment);
  //   } else {
  //     this.expandedShipments.add(shipment);
  //   }
  // }

  // isShipmentExpanded(shipment: any): boolean {
  //   return this.expandedShipments.has(shipment);
  // }

  formatTimeInLocker(timeInLocker: number): string {
    if (!timeInLocker) {
      return "";
    }
    const totalMinutes = Math.ceil(timeInLocker / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours} ${hours === 1 ? "oră" : "ore"} și ${minutes} ${
          minutes === 1 ? "minut" : "minute"}`;
    } else {
      return `${minutes} ${minutes === 1 ? "minut" : "minute"}`;
    }
  }

  generateReport(): void {
    const dialog = this.dialog.open(CBoxProfilePickUpTimesActivityReportGenerateDialogComponent, {
      data: {
        interval: this.filtersForm.value.interval
      },
      minWidth: "min(600px, 100%)",
      disableClose: true,
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      this.loading.set(true);
      this.fetchData();
    });
  }

  openRequestDetails(shipment: (
    CBox_PublicShipmentActivityDetails &
    CBox_PublicShipmentActivityUserPickUpAction
  ) |
  (
    CBox_PublicShipmentActivityDetails &
    CBox_PublicShipmentActivityUserDropOffAction
  )): void {
    switch (shipment.type) {
      case "USER_DROP_OFF":
        this.openDropOffRequestInfo(shipment);
        break;

      case "USER_PICK_UP":
        this.openPickUpRequestInfo(shipment);
        break;

      default:
        console.warn("Unknown shipment type: ", shipment);
    }
  }

  openPickUpRequestInfo(shipment: CBox_PublicShipmentActivityDetails & CBox_PublicShipmentActivityUserPickUpAction): void {
    this.scrollPosition.set(window.scrollY);
    window.scrollTo(0, 0);

    const dialog = this.dialog.open(CBoxProfilePickupRequestInfoComponent, {
      data: shipment.details.request,
      minWidth: "min(1200px, 100%)",
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      window.scrollTo(0, this.scrollPosition());
    });
  }

  openDropOffRequestInfo(shipment: CBox_PublicShipmentActivityDetails & CBox_PublicShipmentActivityUserDropOffAction): void {
    this.scrollPosition.set(window.scrollY);
    window.scrollTo(0, 0);

    const dialog = this.dialog.open(CBoxProfileDropOffRequestInfoComponent, {
      data: shipment.details.request,
      minWidth: "min(1200px, 100%)",
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      window.scrollTo(0, this.scrollPosition());
    });
  }

  override peekData(data: CBox_PublicGetPickUpTimesActivityDataResponse): void {
    this.summary.set(this.aggregateTotalPeriodData(data));
    console.log(this.summary(), "SUMMARY");
  }

  getSearchStructure() {
    return Object.entries({
      "lockers[]": this.filtersForm.value.lockers,
      startDate: this.filtersForm.value.interval[0]?.toISOString(),
      endDate: this.filtersForm.value.interval[1]?.toISOString(),
    })
    .filter(([_, value]) => value?.length || !!value)
    .reduce((acc, [key, value]) => {
      acc[key as keyof CBox_PublicGetAgentActivityDataListParams] = value;
      return acc;
    }, {} as any);
  }

  groupPickUpTimesForItem(
    item: CBox_PublicGetPickUpTimesActivityDataResponse["items"][0]
  ): Bucket[] {
    // Define buckets with appropriate labels and initial values.
    const buckets: Bucket[] = [
      { label: "0-2", count: 0, requests: [], cumulatedPercentageFromTotal: 0 },
      { label: "2-4", count: 0, requests: [], cumulatedPercentageFromTotal: 0 },
      { label: "4-12", count: 0, requests: [], cumulatedPercentageFromTotal: 0 },
      { label: "12-24", count: 0, requests: [], cumulatedPercentageFromTotal: 0 },
      { label: "24-36", count: 0, requests: [], cumulatedPercentageFromTotal: 0 },
      { label: "36+", count: 0, requests: [], cumulatedPercentageFromTotal: 0 },
    ];

    item.pickUpTimes.forEach((pickUpTime) => {
      const { dwellTimeHours, count, requests } = pickUpTime;
      let bucketIndex: number;
      if (dwellTimeHours < 2) {
        bucketIndex = 0;
      } else if (dwellTimeHours < 4) {
        bucketIndex = 1;
      } else if (dwellTimeHours < 12) {
        bucketIndex = 2;
      } else if (dwellTimeHours < 24) {
        bucketIndex = 3;
      } else if (dwellTimeHours < 36) {
        bucketIndex = 4;
      } else {
        bucketIndex = 5;
      }

      buckets[bucketIndex].count += count;
      if (requests && requests.length) {
        buckets[bucketIndex].requests.push(...requests);
      }
    });

    const totalCount = item.totals.pickedUp;
    let cumulativeCount = 0;
    for (let bucket of buckets) {
      cumulativeCount += bucket.count;
      bucket.cumulatedPercentageFromTotal = totalCount ? (cumulativeCount / totalCount) * 100 : 0;
    }

    return buckets;
  }

  aggregateTotalPeriodData(data: CBox_PublicGetPickUpTimesActivityDataResponse): SummaryRow {
    const total: SummaryRow = {
      totalDelivered: 0,
      totalRetrieved: 0,
      totalPickedUp: 0,
      totalCancelled: 0,
      interval_0_2: 0,
      interval_2_4: 0,
      interval_4_12: 0,
      interval_12_24: 0,
      interval_24_36: 0,
      interval_36plus: 0,
    };

    data.items.forEach(item => {
      total.totalDelivered += item.totals.shipments;
      total.totalRetrieved += item.totals.retrieved;
      total.totalPickedUp += item.totals.pickedUp;
      total.totalCancelled += item.totals.cancelled;

      item.pickUpTimes.forEach(pt => {
        if (pt.dwellTimeHours < 2) {
          total.interval_0_2 += pt.count;
        } else if (pt.dwellTimeHours < 4) {
          total.interval_2_4 += pt.count;
        } else if (pt.dwellTimeHours < 12) {
          total.interval_4_12 += pt.count;
        } else if (pt.dwellTimeHours < 24) {
          total.interval_12_24 += pt.count;
        } else if (pt.dwellTimeHours < 48) {
          total.interval_24_36 += pt.count;
        } else {
          total.interval_36plus += pt.count;
        }
      });
    });
    return total;
  }

  calculatePercentFromTotal(value: number, total: number): number {
    const percent = total > 0 ? ((value / total) * 100) : 0;
    return +percent;
  }

  getIntervalTotal(label: string): number | undefined {
    return this.summary()?.[this.mapBucketLabelToSummaryKey(label)];
  }

  private mapBucketLabelToSummaryKey(label: string): keyof SummaryRow {
    switch (label) {
      case "0-2":
        return "interval_0_2";
      case "2-4":
        return "interval_2_4";
      case "4-12":
        return "interval_4_12";
      case "12-24":
        return "interval_12_24";
      case "24-48":
        return "interval_24_36";
      case "48+":
        return "interval_36plus";
      default:
        throw new Error(`Unknown bucket label: ${label}`);
    }
  }
}